import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import {
  ClientQuotaListRow,
  QuotaAllocationListRow,
  QuotaAllocationRequestListRow,
} from '../../../../core/models/card-quota/client-quota.model';
import { SiteQuotaConfig } from '../../../../core/models/card-quota/site-quota-config.model';
import { ClientIInfo } from '../../../../core/models/parking.model';
import { MapperClientQuotaService } from '../../../../core/mappers/card-quota/mapper-client-quota.service';
import { MapperSiteQuotaConfigService } from '../../../../core/mappers/card-quota/mapper-site-quota-config.service';
import { ApiCardQuotaService } from '../../../../core/services/api/card-quota/api-card-quota.service';
import { ClientAccessScopeService } from '../../../../core/services/client-access-scope.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';

@Component({
  selector: 'app-quota-allocation',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, PssLoadingComponent],
  templateUrl: './quota-allocation.component.html',
  styleUrl: './quota-allocation.component.css',
})
export class QuotaAllocationComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  readonly clientScope = inject(ClientAccessScopeService);
  private readonly api = inject(ApiCardQuotaService);
  private readonly mapper = inject(MapperClientQuotaService);
  private readonly siteMapper = inject(MapperSiteQuotaConfigService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);

  private static readonly CLIENT_OPTIONS_PREVIEW = 20;

  siteConfig: SiteQuotaConfig | null = null;
  masterQuotas: ClientQuotaListRow[] = [];
  tenantQuotas: ClientQuotaListRow[] = [];
  clients: ClientIInfo[] = [];
  allocationRows = signal<QuotaAllocationListRow[]>([]);
  allocationRequestRows = signal<QuotaAllocationRequestListRow[]>([]);

  isLoading = signal(false);
  isSaving = signal(false);
  isAllocationsLoading = signal(false);
  isRequestsLoading = signal(false);

  masterQuotaId: number | null = null;
  filterMasterQuotaId: number | null = null;
  tenantClientId: number | null = null;
  allocatedQty = 0;
  note = '';

  clientSearchText = '';
  isClientDropdownOpen = false;

  allocCurrentPage = signal(1);
  allocPageSize = signal(20);
  allocTotalItems = signal(0);
  allocTotalPages = signal(0);

  requestCurrentPage = signal(1);
  requestPageSize = signal(20);
  requestTotalItems = signal(0);
  requestTotalPages = signal(0);
  filterRequestStatus = '';
  pageSizeOptions = [20, 50, 100];
  Math = Math;

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) return;
      untracked(() => this.loadScopeAndPage());
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get allocStartItemIndex(): number {
    if (this.allocTotalItems() === 0) return 0;
    return (this.allocCurrentPage() - 1) * this.allocPageSize() + 1;
  }

  get allocEndItemIndex(): number {
    return Math.min(this.allocCurrentPage() * this.allocPageSize(), this.allocTotalItems());
  }

  get requestStartItemIndex(): number {
    if (this.requestTotalItems() === 0) return 0;
    return (this.requestCurrentPage() - 1) * this.requestPageSize() + 1;
  }

  get requestEndItemIndex(): number {
    return Math.min(this.requestCurrentPage() * this.requestPageSize(), this.requestTotalItems());
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get isViaDistributor(): boolean {
    return this.siteConfig?.distributionMode === 'via_distributor';
  }

  get requiresAllocationApproval(): boolean {
    return this.siteConfig?.allocationRequiresApproval === true;
  }

  get selectedMaster(): ClientQuotaListRow | undefined {
    return this.masterQuotas.find((q) => q.id === this.masterQuotaId);
  }

  get unallocated(): number {
    const m = this.selectedMaster;
    if (!m) return 0;
    return Math.max(0, m.quotaApproved - (m.quotaAllocated ?? 0) - (m.quotaUsed ?? 0));
  }

  get clientDropdownOptions(): ClientIInfo[] {
    const sorted = [...this.clients].sort((a, b) => a.name.localeCompare(b.name, 'th'));
    const q = this.clientSearchText.trim().toLowerCase();
    if (!q) return sorted.slice(0, QuotaAllocationComponent.CLIENT_OPTIONS_PREVIEW);
    return sorted.filter((c) => c.name.toLowerCase().includes(q));
  }

  masterLabel(q: ClientQuotaListRow): string {
    const remaining = Math.max(0, q.quotaApproved - (q.quotaAllocated ?? 0) - (q.quotaUsed ?? 0));
    return `${q.name} — อนุมัติ ${q.quotaApproved} / แจกแล้ว ${q.quotaAllocated ?? 0} / เหลือแจก ${remaining}`;
  }

  actionLabel = (action: string) => this.mapper.allocationActionLabel(action);
  requestStatusLabel = (status: string) => this.mapper.allocationRequestStatusLabel(status);

  ngOnInit(): void {
    if (this.selectedSiteId) this.loadScopeAndPage();
  }

  onRefresh(): void {
    this.loadScopeAndPage(true);
  }

  onSiteChange(siteId: number): void {
    const site = this.siteState.sites().find((s) => s.id === Number(siteId));
    if (!site) return;
    this.siteState.setSelectedSite(site);
  }

  loadScopeAndPage(forceScope = false): void {
    const siteId = this.selectedSiteId;
    const userId = Number(this.cookieService.get('user_id') || 0);
    if (!siteId) {
      this.siteConfig = null;
      this.masterQuotas = [];
      this.tenantQuotas = [];
      this.allocationRows.set([]);
      this.allocationRequestRows.set([]);
      this.clients = [];
      return;
    }

    this.clientScope.load(siteId, userId, forceScope).subscribe({
      next: (state) => {
        this.clients = state.clients;
        if (this.clientScope.isClientLocked()) {
          this.tenantClientId = this.clientScope.lockedClientId();
          this.clientSearchText = this.clientScope.lockedClientName();
        }
        this.loadPage();
      },
      error: () => this.loadPage(),
    });
  }

  onMasterQuotaChange(): void {
    this.filterMasterQuotaId = this.masterQuotaId;
    this.allocCurrentPage.set(1);
    this.loadAllocations();
  }

  onFilterMasterChange(): void {
    this.allocCurrentPage.set(1);
    this.requestCurrentPage.set(1);
    this.loadAllocations();
    this.loadAllocationRequests();
  }

  onFilterRequestStatusChange(): void {
    this.requestCurrentPage.set(1);
    this.loadAllocationRequests();
  }

  setRequestPage(page: number): void {
    if (page >= 1 && page <= this.requestTotalPages()) {
      this.requestCurrentPage.set(page);
      this.loadAllocationRequests();
    }
  }

  get requestPagesArray(): number[] {
    const total = this.requestTotalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.requestCurrentPage() - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  openClientDropdown(): void {
    this.isClientDropdownOpen = true;
    if (this.tenantClientId && !this.clientSearchText.trim()) {
      const selected = this.clients.find((c) => c.id === this.tenantClientId);
      if (selected) this.clientSearchText = selected.name;
    }
  }

  closeClientDropdown(): void {
    this.isClientDropdownOpen = false;
    if (this.tenantClientId) {
      const selected = this.clients.find((c) => c.id === this.tenantClientId);
      if (selected) this.clientSearchText = selected.name;
    }
  }

  onClientSearchInput(): void {
    this.isClientDropdownOpen = true;
    if (!this.clientSearchText.trim()) {
      this.tenantClientId = null;
      return;
    }
    const selected = this.clients.find((c) => c.id === this.tenantClientId);
    if (selected && selected.name.toLowerCase() !== this.clientSearchText.trim().toLowerCase()) {
      this.tenantClientId = null;
    }
  }

  selectClient(client: ClientIInfo): void {
    this.tenantClientId = client.id;
    this.clientSearchText = client.name;
    this.isClientDropdownOpen = false;
  }

  clearClient(): void {
    this.tenantClientId = null;
    this.clientSearchText = '';
    this.isClientDropdownOpen = false;
  }

  setAllocPage(page: number): void {
    if (page >= 1 && page <= this.allocTotalPages()) {
      this.allocCurrentPage.set(page);
      this.loadAllocations();
    }
  }

  onAllocPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.allocPageSize.set(Number(element.value));
    this.allocCurrentPage.set(1);
    this.loadAllocations();
  }

  onRequestPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.requestPageSize.set(Number(element.value));
    this.requestCurrentPage.set(1);
    this.loadAllocationRequests();
  }

  trackAllocId(_index: number, item: QuotaAllocationListRow): number {
    return item.id;
  }

  trackRequestId(_index: number, item: QuotaAllocationRequestListRow): number {
    return item.id;
  }

  trackTenantId(_index: number, item: ClientQuotaListRow): number {
    return item.id;
  }

  get allocPagesArray(): number[] {
    const total = this.allocTotalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.allocCurrentPage() - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  onAllocate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์จัดสรร');
      return;
    }
    if (!this.isViaDistributor) {
      this.toast.error('ลานนี้ไม่ได้ตั้งโหมด via_distributor');
      return;
    }
    if (!this.masterQuotaId || !this.tenantClientId || this.allocatedQty <= 0) {
      this.toast.error('กรุณาเลือกโควต้าหลัก, ร้านค้า และจำนวนที่จัดสรร');
      return;
    }
    if (this.allocatedQty > this.unallocated) {
      this.toast.error(`จำนวนเกินโควต้าที่แจกได้ (คงเหลือ ${this.unallocated})`);
      return;
    }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const payload = this.mapper.mapAllocatePayload(
      {
        masterQuotaId: this.masterQuotaId,
        tenantClientId: this.tenantClientId,
        siteId: this.selectedSiteId,
        allocatedQty: this.allocatedQty,
        note: this.note,
        submitForApproval: this.requiresAllocationApproval,
      },
      userId,
      menuId
    );

    this.isSaving.set(true);
    this.api.allocateQuotaToTenant(payload).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapAllocateResponse(res);
        if (mapped.isSuccess) {
          if (mapped.requiresApproval) {
            this.toast.success(mapped.message || 'ส่งเรื่องจัดสรรเพื่ออนุมัติแล้ว');
          } else {
            this.toast.success(mapped.message || 'จัดสรรสำเร็จ');
          }
          this.allocatedQty = 0;
          this.note = '';
          this.loadPage();
        } else {
          this.toast.error(mapped.message || 'จัดสรรไม่สำเร็จ');
        }
        this.isSaving.set(false);
      },
      error: (err) => {
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาด');
        this.isSaving.set(false);
      },
    });
  }

  private loadPage(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.siteConfig = null;
      this.masterQuotas = [];
      this.tenantQuotas = [];
      this.allocationRows.set([]);
      this.allocationRequestRows.set([]);
      return;
    }

    this.isLoading.set(true);
    this.loadPageData(siteId);
  }

  private loadPageData(siteId: number): void {
    this.api.getSiteQuotaConfig(siteId).subscribe({
      next: (res) => {
        this.siteConfig = res
          ? this.siteMapper.mapConfig(res as SiteQuotaConfig & Record<string, unknown>)
          : null;
      },
      error: () => (this.siteConfig = null),
    });

    this.api.getClientQuotasForTable(siteId, 1, 200, null, 'master', 'active').subscribe({
      next: (res) => {
        const mapped = this.mapper.mapTable(res);
        this.masterQuotas = mapped.items;
        if (!this.masterQuotaId && this.masterQuotas.length === 1) {
          this.masterQuotaId = this.masterQuotas[0].id;
          this.filterMasterQuotaId = this.masterQuotaId;
        }
        this.isLoading.set(false);
        this.loadTenantQuotas();
        this.loadAllocations();
        this.loadAllocationRequests();
      },
      error: () => {
        this.masterQuotas = [];
        this.isLoading.set(false);
      },
    });
  }

  private loadTenantQuotas(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.tenantQuotas = [];
      return;
    }
    this.api.getClientQuotasForTable(siteId, 1, 200, null, 'sub', 'active').subscribe({
      next: (res) => {
        this.tenantQuotas = this.mapper.mapTable(res).items;
      },
      error: () => (this.tenantQuotas = []),
    });
  }

  private loadAllocations(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.allocationRows.set([]);
      return;
    }

    this.isAllocationsLoading.set(true);
    this.api
      .getQuotaAllocationsForTable(
        siteId,
        this.allocCurrentPage(),
        this.allocPageSize(),
        this.filterMasterQuotaId
      )
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapAllocationTable(res);
          this.allocationRows.set(mapped.items);
          this.allocTotalItems.set(mapped.total);
          this.allocTotalPages.set(mapped.totalPages || Math.max(1, Math.ceil(mapped.total / this.allocPageSize())));
          this.isAllocationsLoading.set(false);
        },
        error: () => {
          this.allocationRows.set([]);
          this.isAllocationsLoading.set(false);
          this.toast.error('โหลดประวัติการจัดสรรล้มเหลว');
        },
      });
  }

  private loadAllocationRequests(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.allocationRequestRows.set([]);
      return;
    }

    this.isRequestsLoading.set(true);
    this.api
      .getQuotaAllocationRequestsForTable(
        siteId,
        this.requestCurrentPage(),
        this.requestPageSize(),
        this.filterMasterQuotaId,
        this.filterRequestStatus || null
      )
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapAllocationRequestTable(res);
          this.allocationRequestRows.set(mapped.items);
          this.requestTotalItems.set(mapped.total);
          this.requestTotalPages.set(
            mapped.totalPages || Math.max(1, Math.ceil(mapped.total / this.requestPageSize()))
          );
          this.isRequestsLoading.set(false);
        },
        error: () => {
          this.allocationRequestRows.set([]);
          this.isRequestsLoading.set(false);
        },
      });
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }
}
